import React, { Fragment, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
} from "@material-ui/core";
import { AccountCircle, Close, Delete, Edit, Save } from "@material-ui/icons";
import AlternateEmail from "@material-ui/icons/AlternateEmail";
import moment from "moment";
import ApiClient from "utils/ApiClient";
import { handleCommentsData } from "app/websocketManager/eventEmitter";

const CommentsPopover = ({ open, anchorEl, updateCount, list_id, row_id, closePopper, uniqueId, commentsList = [], fromRowViewPanel = false}) => {
  const [anchorElMentions, setAnchorElMentions] = useState(null);
  const [comments, setComments] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [isUpdated, setIsUpdated] = useState(false);
  const scrollRef = useRef(null);
  const focusRef = useRef(null);

  const [commentsData, setCommentsData] = useState({
    input: "",
    loading: false,
    addingComment: false,
  });

  const showAlert = (msg, type) => {
    window.appStore.notificationStore.showNotification(msg, type);
  };

  const handleMentions = (address) => {
    if (typeof address == "string") {
      setCommentsData({ ...commentsData, input: commentsData.input + ` @${address}` });
    }
  };
  const fetchMentions = async () => {
    try {
      const resp = await ApiClient.Comments.getListMentions({
        list_id: list_id,
        is_web: 1,
      });
      setMentions(resp.data);
    } catch (error) {
      setMentions([]);
    }
  };
  const fetchComments = async (load = false) => {
    try {
      setCommentsData({ ...commentsData, loading: load, input: "" });
      const res = await ApiClient.Comments.getComments({
        list_id: list_id,
        row_id: row_id,
        page: 1,
        start: 0,
        limit: 1000,
        is_web: 1,
      });
      setComments(res.data);
      setCommentsData({
        input: "",
        loading: false,
        addingComment: false,
      });
      updateCount(row_id, res.data.length);
      scrollRef.current.scrollIntoView();
      load && focusRef.current.focus();
      return res.data;
    } catch (e) {
      // console.log(e);
      setCommentsData({ ...commentsData, loading: false, addingComment: false });
      showAlert("Something went wrong while fetching comments.", "error");
    }
  };

  const handleClosePopper = () => {
    if (fromRowViewPanel) {
      closePopper(comments, commentsList, isUpdated);
    } else {
      closePopper(comments);
    }
  };

  const handleClickMentions = (event) => {
    setAnchorElMentions(event.currentTarget);
  };

  const handleCloseMentions = () => {
    setAnchorElMentions(null);
  };
  const handleSubmit = async (event, id = -1, description = commentsData.input) => {
    event && event.preventDefault();
    if (description.length < 1) {
      return;
    }
    try {
      setCommentsData({ ...commentsData, addingComment: true });
      let body = {
        list_id: list_id,
        row_id: row_id,
        description,
        is_web: 1,
        comment_id: id,
        from_sheet: true
      };
      await ApiClient.Comments.save(body);
      !isUpdated && setIsUpdated(true);
      const resp = await fetchComments();
      handleCommentsData(JSON.stringify(resp.reverse()), resp.length, row_id, uniqueId, list_id);
    } catch (e) {
      if (e?.response?.success == false) {
        showAlert(e?.response?.message ?? "Something went wrong while adding comment.", "error");
      }
      setCommentsData({ ...commentsData, addingComment: false });
    }
  };

  const deleteComment = async (cmnt) => {
    if (cmnt.created_by != window.LOGGED_IN_USER.user_id) {
      showAlert("You don't have permission to delete this comment.", "error");
      return;
    }
    try {
      await window.appStore.notificationStore.confirm({
        description: <>This will delete this comment</>,
      });
      await ApiClient.Comments.delete({
        list_id: list_id,
        comment_id: cmnt.id,
        is_web: 1,
        from_sheet: true
      });
      !isUpdated && setIsUpdated(true);
      const resp = await fetchComments();
      handleCommentsData(JSON.stringify(resp.reverse()), resp.length, row_id, uniqueId, list_id);
    } catch (e) {
      if (e?.response?.success == false) {
        showAlert(
          e?.response?.message ?? "You don't have permission to delete this comment.",
          "error"
        );
      }
    }
  };

  const updateComment = (cmnt, text) => {
    if (cmnt.created_by != window.LOGGED_IN_USER.user_id) {
      showAlert("You don't have permission to edit this comment.", "error");
      return;
    }
    handleSubmit(false, cmnt.id, text);
  };

  useEffect(() => {
    if (!fromRowViewPanel) {
      fetchComments(true);
    } else {
      setComments(commentsList.slice().reverse());
    }
    fetchMentions();
  }, []);

  const openMentions = Boolean(anchorElMentions);
  const id = open ? "simple-popover" : undefined;

  return (
    <Popover
      id={id}
      open={open}      
      onClose={handleClosePopper}
      {
        ...(fromRowViewPanel
        ? {
            anchorReference: "anchorPosition",
            anchorPosition: {
              top: window.innerHeight / 2,
              left: window.innerWidth / 2,
            },
            transformOrigin: {
              vertical: "center",
              horizontal: "center",
            },
          }
        : {
            anchorEl: anchorEl,
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            }
          }
        )
      }
    >
      <StyledPopperGrid container direction="column">
        <StyledHeaderGrid item container>
          <Grid item>
            <h5>Comments</h5>
          </Grid>
          <Grid item style={{ flex: "1" }}></Grid>
          <Grid item>
            <IconButton onClick={handleClosePopper}>
              <Close />
            </IconButton>
          </Grid>
        </StyledHeaderGrid>
        {!commentsData.loading ? (
          <StyledGrd container item>
            {comments.length > 0 &&
              comments
                ?.map((comment) => (
                  <Comment
                    key={comment.id}
                    comment={comment}
                    deleteComment={deleteComment}
                    updateComment={updateComment}
                    enableActionButtons={comment.created_by == window.LOGGED_IN_USER.user_id}
                  />
                ))
                .reverse()}
            <div ref={scrollRef}></div>
          </StyledGrd>
        ) : (
          <StyledGrd container item alignItems="center" justifyContent="center">
            <CircularProgress />
          </StyledGrd>
        )}
        <StyledInputWrapper item container>
          <form style={{ flex: "1" }} onSubmit={handleSubmit}>
            {!commentsData.addingComment ? (
              <TextField
                onKeyDown={(e) => {
                  if (e.keyCode == 13 && e.shiftKey == false) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                multiline
                rows={2}
                className="without_padding"
                maxRows={2}
                value={commentsData.input}
                onChange={(e) => {
                  setCommentsData({ ...commentsData, input: e.target.value });
                }}
                variant="outlined"
                fullWidth
                // label="Add Comment"
                inputRef={focusRef}
              />
            ) : (
              <Grid direction="row" item container alignItems="center" justifyContent="center">
                <CircularProgress size="20px" />
              </Grid>
            )}
          </form>
          <div>
            <IconButton
              aria-label="more"
              aria-controls="long-menu"
              aria-haspopup="true"
              onClick={handleClickMentions}
            >
              <Tooltip title="Using @ mentions, you can notify someone of your comment.">
                <AlternateEmail />
              </Tooltip>
            </IconButton>
            <Menu
              id="long-menu"
              anchorEl={anchorElMentions}
              keepMounted
              open={openMentions}
              onClose={handleCloseMentions}
              PaperProps={{
                style: {
                  maxHeight: 48 * 4.5,
                  overflowY: "auto",
                },
              }}
            >
              {mentions.map((el) => {
                return (
                  <MenuItem key={Math.random()} onClick={() => handleMentions(el.address)}>
                    {el.address}
                  </MenuItem>
                );
              })}
            </Menu>
          </div>
        </StyledInputWrapper>
      </StyledPopperGrid>
    </Popover>
  );
};

export default CommentsPopover;

const Comment = ({ comment, deleteComment, updateComment, enableActionButtons }) => {
  let date = new Date(comment.created_at);
  const currentOffset = new Date().getTimezoneOffset();
  const offsetDifference = -330;
  const final = currentOffset - offsetDifference
  const finalDate = date.setMinutes(date.getMinutes() - final);

  const [commentData, setCommentData] = useState({
    visible: false,
    editMode: false,
    editedValue: "",
  });

  const onClickEdit = () => {
    setCommentData({ ...commentData, editMode: true, editedValue: comment.description });
  };

  const onClickSave = async () => {
    setCommentData({ ...commentData, editMode: false });
    if (commentData.editedValue.length == 0 || commentData.editedValue == comment.description) {
      return;
    } else {
      updateComment(comment, commentData.editedValue);
    }
  };

  const onClickDelete = () => {
    deleteComment(comment);
  };

  return (
    <StyledGridMain
      item
      container
      key={comment.id}
      onMouseEnter={() => setCommentData({ ...commentData, visible: true })}
      onMouseLeave={() => setCommentData({ ...commentData, visible: false })}
      backgroundColor={commentData.visible ? "#ebebeb" : ""}
    >
      <StyledCommentWrapper item container>
        <Grid item>
          <StyledAccountCircle />
        </Grid>
        <StyledComment item container>
          <StyledCommentHead item container>
            <h6>{comment.created_by_name}</h6>
            <Tooltip title={moment(finalDate).format("DD-MMM-YY HH:mm a")}>
            <StyledDateDiv>{moment(finalDate).fromNow()}</StyledDateDiv>
            </Tooltip>
          </StyledCommentHead>
          <StyledCommentEditorGrid item>
            {commentData.editMode ? (
              <TextField
                className="without_padding"
                multiline
                variant="outlined"
                size="small"
                fullWidth
                value={commentData.editedValue}
                onChange={(e) => setCommentData({ ...commentData, editedValue: e.target.value })}
              />
            ) : (
              <div style={{ whiteSpace: "pre-line" }}>{comment.description}</div>
            )}
          </StyledCommentEditorGrid>
        </StyledComment>
      </StyledCommentWrapper>
      {enableActionButtons && (
        <StyledActionButtonGrid container visible={commentData.visible}>
          {!commentData.editMode ? (
            <Fragment>
              <StyledIconButton onClick={onClickEdit} size="small">
                <StyledEdit />
              </StyledIconButton>
              <StyledIconButton onClick={onClickDelete} size="small">
                <StyledDelete />
              </StyledIconButton>
            </Fragment>
          ) : (
            <Fragment>
              <StyledIconButton onClick={onClickSave} size="small">
                <StyledSave />
              </StyledIconButton>
              <StyledIconButton
                onClick={() => setCommentData({ ...commentData, editMode: false })}
                size="small"
              >
                <StyledClose />
              </StyledIconButton>
            </Fragment>
          )}
        </StyledActionButtonGrid>
      )}
    </StyledGridMain>
  );
};

const StyledHeaderGrid = styled(Grid)`
  && {
    display: flex;
    flex-direction: row;
    margin: 5px;
    align-items: center;
  }
`;

const StyledInputWrapper = styled(Grid)`
  && {
    display: flex;
    flex-direction: row;
    margin-top: 8px;
    align-items: center;
    justify-content: center;
  }
`;

const StyledAccountCircle = styled(AccountCircle)`
  && {
    color: #237ff4;
    font-size: 2rem;
  }
`;

const StyledComment = styled(Grid)`
  && {
    display: flex;
    flex-direction: column;
    padding-left: 5px;
  }
`;

const StyledCommentHead = styled(Grid)`
  && {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    flex: 1;
  }
`;

const StyledCommentEditorGrid = styled(Grid)`
  && {
    display: flex;
    margin-right: 5px;
  }
`;

const StyledEdit = styled(Edit)`
  && {
    color: #237ff4;
    font-size: 15px;
  }
`;

const StyledDelete = styled(Delete)`
  && {
    color: red;
    font-size: 15px;
  }
`;

const StyledSave = styled(Save)`
  && {
    color: #237ff4;
    font-size: 15px;
  }
`;

const StyledClose = styled(Close)`
  && {
    color: red;
    font-size: 15px;
  }
`;

const StyledIconButton = styled(IconButton)`
  && {
    margin: 4px 10px 0 0;
  }
`;

const StyledGridMain = styled(Grid)`
  && {
    display: flex;
    flex-direction: column;
    padding: 7px 0px 7px 5px;
    border-radius: 5px;
    background-color: ${(props) => (props.backgroundColor ? "#ebebeb" : "")};
  }
`;

const StyledActionButtonGrid = styled(Grid)`
  && {
    display: flex;
    flex-direction: row;
    flex: 1;
    justify-content: flex-end;
    opacity: ${(props) => (props.visible ? "1" : "0")};
  }
`;

const StyledCommentWrapper = styled(Grid)`
  && {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
  }
`;

const StyledGrd = styled(Grid)`
  && {
    display: flex;
    flex-direction: column;
    flex: 1;
    flex-wrap: nowrap;
    overflow: auto;
    min-height: 50vh;
    max-height: 55vh;
  }
`;

const StyledPopperGrid = styled(Grid)`
  && {
    background-color: white;
    overflow: hidden;
    radius: 10px;
    padding: 10px;
    width: 400px;
    box-shadow: 0px 0px 10px 1px rgba(0, 0, 0, 0.2);
    border-radius: 5px;
  }
`;

const StyledDateDiv = styled.div`
  font-size: 9px;
  padding-left: 5px;
`;
